import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { useThreads } from '@dev-streetbeat/chat/react'
import type { Thread } from '@dev-streetbeat/chat/types'
import { ThreadListPanel } from '@dev-streetbeat/chat-ui'
import { ChevronRight, PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import { brandAssetUrl } from '@/runtime'
import { cn } from '@/lib/utils'
import { formatThreadTitle } from '@/lib/threadRoleClassifier'
import { ChatStatusIndicator } from './ChatStatusIndicator'
import styles from './DesignerSidebar.module.scss'

const COLLAPSED_WIDTH = 52
const DEFAULT_WIDTH = 240
const MIN_EXPANDED_WIDTH = 180
const COLLAPSE_THRESHOLD = 100
const CHAT_HISTORY_REFRESH_INTERVAL_MS = 4000

interface SidebarContextValue {
  collapsed: boolean
  width: number
  isDragging: boolean
  isTransitioning: boolean
  toggle: () => void
  startResize: (event: MouseEvent) => void
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  width: DEFAULT_WIDTH,
  isDragging: false,
  isTransitioning: false,
  toggle: () => {},
  startResize: () => {},
})

export interface DesignerSidebarUser {
  initials: string
  displayName: string
  href: string
  active: boolean
}

export interface DesignerSidebarItem {
  href: string
  icon: LucideIcon
  label: string
  title?: string
  active: boolean
  end?: boolean
  /**
   * When true, paint a small red dot on the top-right of the icon to
   * signal that someone else has pushed new items behind this route
   * (e.g. a branch / area manager queued new todos for the banker).
   * Purely presentational — the link still navigates to `href` and the
   * screen-reader text is appended to the label. Do NOT set this from a
   * generic "has items" count; the dot is reserved for an unread /
   * pushed-by-other signal.
   */
  hasNotification?: boolean
  /**
   * Optional number to render in a small pill next to the label when the
   * sidebar is expanded. Used today as a steady-state count badge (e.g.
   * the banker's active-todo count). Independent of `hasNotification` —
   * a pill can exist without a dot, and a dot without a pill.
   */
  notificationCount?: number
}

export interface DesignerSidebarGroup {
  label: string
  items: DesignerSidebarItem[]
}

export interface ChatHistoryProps {
  onNewChat: () => void
  onThreadChange: (threadId: string | null, thread?: Thread | null) => void
  onThreadDeleteStart?: (threadId: string) => void
  onThreadDeleteError?: (threadId: string, error: unknown) => void
  selectedThreadId: string | null
  /**
   * When true, the "Nuova chat" button paints as the current selection
   * (accent dot + label). Used while the dashboard sits in its focused
   * composer state — before the first send creates a real thread — so
   * the sidebar's selection state tracks the chat context, not the
   * Dashboard nav item.
   */
  isNewChatActive?: boolean
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [isDragging, setIsDragging] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionTimer = useRef<number | null>(null)

  const toggle = useCallback(() => {
    // Set the transitioning flag in the same render as the collapsed flip so
    // React batches both state changes. This prevents a single-frame glitch
    // where data-collapsed='true' is committed but pointer-events: none is
    // not yet applied — that frame is where the cursor still over the toggle
    // button would trigger the collapsed :hover rule on the logo.
    setIsTransitioning(true)
    if (transitionTimer.current !== null) {
      window.clearTimeout(transitionTimer.current)
    }
    transitionTimer.current = window.setTimeout(() => {
      setIsTransitioning(false)
      transitionTimer.current = null
    }, 250)

    setCollapsed((current) => {
      if (current) {
        setWidth((previous) => (previous <= COLLAPSED_WIDTH ? DEFAULT_WIDTH : previous))
      }
      return !current
    })
  }, [])

  useEffect(() => {
    return () => {
      if (transitionTimer.current !== null) {
        window.clearTimeout(transitionTimer.current)
      }
    }
  }, [])

  const startResize = useCallback((event: MouseEvent) => {
    event.preventDefault()
    setIsDragging(true)

    const maxWidth = Math.max(DEFAULT_WIDTH, window.innerWidth * 0.3)

    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const nextWidth = moveEvent.clientX
      if (nextWidth < COLLAPSE_THRESHOLD) {
        setCollapsed(true)
        setWidth(COLLAPSED_WIDTH)
        return
      }

      setCollapsed(false)
      setWidth(Math.max(MIN_EXPANDED_WIDTH, Math.min(nextWidth, maxWidth)))
    }

    const onMouseUp = () => {
      setIsDragging(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])

  return (
    <SidebarContext.Provider
      value={{ collapsed, width, isDragging, isTransitioning, toggle, startResize }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}

export function DesignerSidebar({
  chatHistory,
  footerAction,
  groups,
  homeLabel,
  logo,
  showGroupHeaders = true,
  user,
}: {
  chatHistory?: ChatHistoryProps
  footerAction?: ReactNode
  groups: DesignerSidebarGroup[]
  homeLabel: string
  logo?: ReactNode
  showGroupHeaders?: boolean
  user?: DesignerSidebarUser | null
}) {
  const { collapsed, width, isDragging, isTransitioning, toggle, startResize } = useSidebar()
  const visibleGroups = groups.filter((group) => group.items.length > 0)

  return (
    <aside
      className={cn(styles.sidebar, !isDragging && styles.sidebarAnimated)}
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        width: collapsed ? COLLAPSED_WIDTH : width,
        pointerEvents: isTransitioning ? 'none' : undefined,
      }}
    >
      <div className={styles.header}>
        {collapsed ? (
          <button
            type="button"
            onClick={toggle}
            className={styles.logoExpandButton}
            aria-label="Espandi menu"
            aria-expanded={false}
            title="Espandi menu"
          >
            <span className={styles.collapsedLogoMark}>
              {logo ?? <DefaultLogoMark collapsed />}
            </span>
            <PanelLeftOpen className={styles.collapsedExpandIcon} size={16} aria-hidden />
          </button>
        ) : (
          <>
            <Link to="/" aria-label={homeLabel} className={styles.logoLink}>
              {logo ?? <DefaultLogoMark collapsed={false} />}
            </Link>
            <button
              type="button"
              onClick={toggle}
              className={styles.iconButton}
              aria-label="Comprimi menu"
              aria-expanded
              title="Comprimi menu"
            >
              <PanelLeftClose size={16} aria-hidden />
            </button>
          </>
        )}
      </div>

      <nav className={styles.nav} aria-label="Principale">
        {visibleGroups.map((group) => (
          <SidebarGroup key={group.label} label={group.label} showHeader={showGroupHeaders}>
            {group.items.map((item) => (
              <SidebarItem key={item.href} item={item} />
            ))}
          </SidebarGroup>
        ))}
        {chatHistory ? (
          <ChatHistorySection
            onNewChat={chatHistory.onNewChat}
            onThreadChange={chatHistory.onThreadChange}
            onThreadDeleteStart={chatHistory.onThreadDeleteStart}
            onThreadDeleteError={chatHistory.onThreadDeleteError}
            selectedThreadId={chatHistory.selectedThreadId}
            isNewChatActive={chatHistory.isNewChatActive ?? false}
          />
        ) : null}
      </nav>

      {(user || footerAction) && (
        <div className={styles.footer}>
          <div className={styles.footerRow}>
            {user ? <UserRow user={user} /> : null}
            {footerAction ? <div className={styles.footerAction}>{footerAction}</div> : null}
          </div>
        </div>
      )}

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize navigation"
        className={styles.resizeHandle}
        onMouseDown={startResize}
      />
    </aside>
  )
}

function DefaultLogoMark({ collapsed }: { collapsed: boolean }) {
  return (
    <>
      <span className={styles.logoVisual} aria-hidden>
        <img
          src={brandAssetUrl('credem-logo.svg')}
          alt=""
          className={styles.logoMark}
          draggable={false}
        />
        {!collapsed ? <span className={styles.logoText}>Credem</span> : null}
      </span>
      <span className={styles.srOnly}>Credem</span>
    </>
  )
}

function SidebarGroup({
  children,
  label,
  showHeader,
}: {
  children: ReactNode
  label: string
  showHeader: boolean
}) {
  const { collapsed } = useSidebar()
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (!collapsed) setIsOpen(true)
  }, [collapsed])

  const showChildren = collapsed || isOpen

  return (
    <section className={cn(styles.group, !showHeader && styles.groupFlat)}>
      {showHeader ? (
        <button
          type="button"
          className={styles.groupButton}
          onClick={collapsed ? undefined : () => setIsOpen((open) => !open)}
          aria-expanded={collapsed ? undefined : isOpen}
          aria-label={label}
        >
          {collapsed ? (
            <span className={styles.collapsedRule} aria-hidden />
          ) : (
            <>
              <span className={styles.groupLabel}>{label}</span>
              <ChevronRight
                className={cn(styles.groupChevron, isOpen && styles.groupChevronOpen)}
                aria-hidden
              />
            </>
          )}
        </button>
      ) : null}
      {showChildren ? <div className={styles.groupItems}>{children}</div> : null}
    </section>
  )
}

function SidebarItem({ item }: { item: DesignerSidebarItem }) {
  const { collapsed } = useSidebar()
  const Icon = item.icon
  const ariaLabel = item.hasNotification
    ? item.notificationCount != null && item.notificationCount > 0
      ? `${item.label} (${item.notificationCount} nuovi elementi)`
      : `${item.label} (elementi non letti)`
    : item.label

  return (
    <Link
      to={item.href}
      title={collapsed ? item.title || item.label : item.title}
      aria-label={ariaLabel}
      aria-current={item.active ? 'page' : undefined}
      className={cn(styles.item, item.active && styles.itemActive)}
    >
      <span className={styles.itemIconWrap}>
        <Icon className={styles.itemIcon} aria-hidden />
        {item.hasNotification ? (
          <span className={styles.itemNotificationDot} aria-hidden />
        ) : null}
      </span>
      {!collapsed ? (
        <span className={styles.itemLabelRow}>
          <span className={styles.itemLabel}>{item.label}</span>
          {item.notificationCount != null && item.notificationCount > 0 ? (
            <span className={styles.itemNotificationCount} aria-hidden>
              {item.notificationCount}
            </span>
          ) : null}
        </span>
      ) : null}
    </Link>
  )
}

function ChatHistorySection({
  onNewChat,
  onThreadChange,
  onThreadDeleteStart,
  onThreadDeleteError,
  selectedThreadId,
  isNewChatActive,
}: {
  onNewChat: () => void
  onThreadChange: (threadId: string | null, thread?: Thread | null) => void
  onThreadDeleteStart?: (threadId: string) => void
  onThreadDeleteError?: (threadId: string, error: unknown) => void
  selectedThreadId: string | null
  isNewChatActive: boolean
}) {
  const { collapsed } = useSidebar()
  const { refreshThreads, threads } = useThreads()
  const [isOpen, setIsOpen] = useState(true)
  const isHistoryEmpty = threads.length === 0

  useEffect(() => {
    void refreshThreads()
  }, [refreshThreads, selectedThreadId])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshThreads()
      }
    }, CHAT_HISTORY_REFRESH_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [refreshThreads])

  return (
    <div className={styles.chatSection} hidden={isHistoryEmpty}>
      <div className={styles.chatDivider} aria-hidden />

      <button
        type="button"
        className={cn(styles.chatNewButton, isNewChatActive && styles.chatNewButtonActive)}
        onClick={onNewChat}
        aria-label="Nuova chat"
        aria-current={isNewChatActive ? 'page' : undefined}
        title={collapsed ? 'Nuova chat' : undefined}
      >
        <Plus className={styles.itemIcon} aria-hidden />
        {!collapsed ? <span className={styles.itemLabel}>Nuova Chat</span> : null}
      </button>

      <section className={styles.group} hidden={collapsed}>
        <button
          type="button"
          className={styles.groupButton}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-label="Storico chat"
        >
          <span className={styles.groupLabel}>Storico chat</span>
          <ChevronRight
            className={cn(styles.groupChevron, isOpen && styles.groupChevronOpen)}
            aria-hidden
          />
        </button>

        <div className={styles.chatHistoryPanel} hidden={!isOpen}>
          <ThreadListPanel
            pageSize={30}
            threadStatus={{ renderIndicator: (state) => <ChatStatusIndicator state={state} /> }}
            onThreadChange={onThreadChange}
            onThreadDeleteStart={onThreadDeleteStart}
            onThreadDelete={(deletedThreadId) => {
              if (deletedThreadId === selectedThreadId) {
                onThreadChange(null, null)
              }
            }}
            onThreadDeleteError={onThreadDeleteError}
            resolveThreadTitle={(thread) => formatThreadTitle(thread.title)}
            searchPlaceholder="Cerca nelle chat..."
            searchEmptyText="Nessuna conversazione trovata"
            emptyStateText="Nessuna conversazione"
            loadingText=""
            loadingMoreText=""
            untitledThreadText="Nuova conversazione"
            deleteDialogTitle="Elimina conversazione"
            deleteDialogMessage="Vuoi eliminare questa conversazione dalla cronologia?"
            deleteDialogCancelText="Annulla"
            deleteDialogConfirmText="Elimina"
            deleteDialogClassName={styles.threadHistoryDeleteDialog}
            historyItemClassName={styles.chatSessionItem}
            enableSnippets
          />
        </div>
      </section>
    </div>
  )
}

function UserRow({ user }: { user: DesignerSidebarUser }) {
  const { collapsed } = useSidebar()

  return (
    <Link
      to={user.href}
      aria-label={user.displayName}
      aria-current={user.active ? 'page' : undefined}
      className={cn(styles.user, user.active && styles.userActive)}
      title={collapsed ? user.displayName : undefined}
    >
      <span className={styles.avatar} aria-hidden>
        {user.initials}
      </span>
      {!collapsed ? (
        <span className={styles.userText}>
          <span className={styles.userName}>{user.displayName}</span>
        </span>
      ) : null}
    </Link>
  )
}
